// src/components/ui/BottomSheet.tsx
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string;
  fullScreen?: boolean;
}

/**
 * Renders a bottom-up sheet via a portal to <body>,
 * prevents page scroll when open, and adds a backdrop + close button.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  fullScreen = false,
  height,
}) => {
  // lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    // ensure unlocked if unmounted while closed
    document.body.style.overflow = '';
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-6">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      <div
        className={`
         relative w-full bg-white rounded-t-xl
         pt-4 pb-10 overflow-y-auto
         px-4
        `}
        style={{
          paddingBottom: `calc(env(safe-area-inset-bottom) + 2.5rem)`,
          // if caller set height, use it; otherwise cap at 90vh
          ...(height
            ? { height }
            : { maxHeight: fullScreen ? '100vh' : '90vh' }),
        }}
      >
        {/* close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 8.586l4.95-4.95a1 1 0 011.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10l-4.95-4.95A1 1 0 015.05 3.636L10 8.586z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* your sheet content */}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
